import type { FC } from 'react';
import React from 'react';
import { Box, Divider, Stack, Typography } from '@packages/shared';
import { ArrowMediumRight32, Checkmark32 } from '@packages/themes/icons';
import type { SelectValuesFragmentFragment } from '@packages/gql/generated/shopping/graphql';
import { ColorTile } from '../ColorTile';
import { getIsAvailable } from '../../../../helpers/getIsAvailable/getIsAvailable';
import {
  indicatorDefault,
  indicatorDisabled,
  variationNameDefault,
  variationNameDisabled,
} from './styles';

export type DialogVariationSelectionRowProps = {
  /**
   * The GraphQL value data for the given product
   */
  value: SelectValuesFragmentFragment['values'][number];
  /**
   * indicates if one value has an image and the column has to be rendered
   */
  hasImage: boolean;
  /**
   * method that will be executed on variation click
   * @param sku the sku of the clicked variation
   * @returns void
   */
  clickHandler: (sku: SelectValuesFragmentFragment['values'][number]['sku']) => void;
};

/**
 * DialogVariationSelectionRow component
 *
 * used to display a dimension value in the dialog
 *
 * */
export const DialogVariationSelectionRow: FC<DialogVariationSelectionRowProps> = ({
  value: { displayText, image, isSelected, rgb, variationMainImage, sku, availability },
  hasImage,
  clickHandler,
}) => {
  const isDisabled = availability ? !getIsAvailable(availability.state) : false;
  const isClickable = !(isSelected === true);
  let backgroundColor = 'common.white';
  let icon = <ArrowMediumRight32 />;
  if (isSelected) {
    backgroundColor = 'grey.light';
    icon = <Checkmark32 />;
  }

  return (
    <>
      <Stack
        sx={{
          flexDirection: 'row',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 1.5,
          p: 1.5,
          backgroundColor,
          cursor: isClickable ? 'pointer' : 'auto',
          ':hover': {
            backgroundColor: 'grey.light',
          },
        }}
        onClick={() => {
          if (isClickable) {
            clickHandler(sku);
          }
        }}
      >
        {hasImage && (
          <ColorTile
            imageSize="DIALOG"
            rgb={rgb}
            image={variationMainImage || image}
            displayText={displayText}
          />
        )}
        <Typography variant="body2" sx={isDisabled ? variationNameDisabled : variationNameDefault}>
          {displayText}
        </Typography>
        {isDisabled ? (
          <Box sx={indicatorDisabled}>
            <Typography variant="body2" color="error.main" component="strong">
              {availability!.message}
            </Typography>
          </Box>
        ) : (
          <Box sx={indicatorDefault}>{icon}</Box>
        )}
      </Stack>
      <Divider />
    </>
  );
};
